import type { NotificationPayload } from 'react-admin'
import { __t } from '../i18n/t'

/**
 * Diz ao usuário, com o nome que ele vê na tela, o que corrigir antes de salvar (pedido ao dev #10).
 *
 * Antes: erro do servidor saía com o nome técnico ("Revise: complement: Campo não permitido") e o
 * erro de validação da própria tela saía genérico ("O formulário não é válido..."), sem dizer o
 * campo nem a aba. Em formulário com abas o campo vermelho costuma estar em outra aba.
 *
 * Lê a marcação que o react-admin 5 já renderiza, então vale para todo formulário sem mexer em
 * cada tela:
 * - toda entrada tem um contêiner com a classe `ra-input` (TextInput, NumberInput, SelectInput,
 *   AutocompleteInput, BooleanInput, DateInput, ArrayInput) e o `<input>` tem `name` = caminho do
 *   campo (`street`, `coordinates.1.longitude`), o mesmo que o backend devolve em `errors`;
 * - o painel da aba (`FormTab`) tem `aria-labelledby="tabheader-N"`, o id do cabeçalho da aba;
 * - campo com erro tem a classe `Mui-error` no rótulo e no texto de ajuda.
 * O texto lido já está traduzido (é o que está na tela).
 */

export interface FieldOnScreen {
  label: string
  tab?: string
  /** Posição (1, 2, ...) dentro de uma lista (ArrayInput), quando o campo é de um item. */
  item?: number
}

export interface InvalidFieldOnScreen extends FieldOnScreen {
  message?: string
}

const INPUT = '.ra-input'
const LABELS = 'label, .MuiInputLabel-root, .MuiFormControlLabel-label, .MuiFormLabel-root'
// Caminho vindo do backend passa pela mesma regra de lá (SAFE_PATH); aqui ainda vira seletor CSS.
const SAFE_PATH = /^[\w.\-[\]]{1,80}$/

/** Tira o asterisco de obrigatório e espaços sobrando ("Rua *" -> "Rua"). */
export const cleanLabel = (text?: string | null): string =>
  (text || '').replace(/\s+/g, ' ').replace(/[\s*]+$/, '').trim()

/** Texto do elemento sem o de `<style>`/`<script>` internos (o emotion põe `<style>` no meio no SSR). */
const textOf = (el: Element | null | undefined): string => {
  if (!el) return ''
  let text = el.textContent || ''
  for (const inner of Array.from(el.querySelectorAll('style, script'))) {
    if (inner.textContent) text = text.replace(inner.textContent, '')
  }
  return text
}

/** Último índice numérico do caminho, contado a partir de 1: `coordinates.1.longitude` -> 2. */
export const itemOf = (path?: string | null): number | undefined => {
  const numbers = (path || '').split('.').filter(part => /^\d+$/.test(part))
  return numbers.length ? Number(numbers[numbers.length - 1]) + 1 : undefined
}

const ownLabel = (wrapper: Element): string => {
  for (const el of Array.from(wrapper.querySelectorAll(LABELS))) {
    // Rótulo de outra entrada dentro desta (itens de um ArrayInput) não vale.
    if (el.closest(INPUT) !== wrapper) continue
    const text = cleanLabel(textOf(el))
    if (text) return text
  }
  return ''
}

/** Item de lista sem rótulo próprio (`<NumberInput label={false} />`) usa o rótulo da lista. */
const labelOf = (wrapper: Element): string => {
  const own = ownLabel(wrapper)
  if (own) return own
  const list = wrapper.parentElement?.closest(INPUT)
  return list ? ownLabel(list) : ''
}

const tabOf = (el: Element, doc: Document): string | undefined => {
  const panel = el.closest('[aria-labelledby^="tabheader-"]')
  const headerId = panel?.getAttribute('aria-labelledby')
  const header = headerId ? doc.getElementById(headerId) : null
  return cleanLabel(textOf(header)) || undefined
}

const errorTextOf = (wrapper: Element): string => {
  for (const el of Array.from(wrapper.querySelectorAll('.MuiFormHelperText-root.Mui-error'))) {
    if (el.closest(INPUT) !== wrapper) continue
    const text = cleanLabel(textOf(el))
    if (text) return text
  }
  return ''
}

const currentDocument = (): Document | undefined =>
  typeof document === 'undefined' ? undefined : document

/** Rótulo, aba e item do campo `path` no formulário aberto; null se ele não está na tela. */
export const findFieldOnScreen = (path: string, doc: Document | undefined = currentDocument()): FieldOnScreen | null => {
  if (!doc || !SAFE_PATH.test(path)) return null
  const forms = Array.from(doc.querySelectorAll('form'))
  const input = forms.map(form => form.querySelector(`[name="${path}"]`)).find(Boolean)
    ?? doc.querySelector(`[name="${path}"]`)
  // Lista (ArrayInput) não tem <input> com o próprio nome: acha pelo contêiner.
  const wrapper = input?.closest(INPUT) ?? doc.querySelector(`[class~="ra-input-${path}"]`)
  if (!wrapper) return null
  const label = labelOf(wrapper)
  if (!label) return null
  return { label, tab: tabOf(wrapper, doc), item: itemOf(path) }
}

/** Campos marcados em vermelho nos formulários da tela, na ordem em que aparecem. */
export const collectInvalidFields = (doc: Document | undefined = currentDocument()): InvalidFieldOnScreen[] => {
  if (!doc) return []
  const wrappers: Element[] = []
  for (const form of Array.from(doc.querySelectorAll('form'))) {
    for (const marked of Array.from(form.querySelectorAll('.Mui-error'))) {
      const wrapper = marked.closest(INPUT)
      if (wrapper && !wrappers.includes(wrapper)) wrappers.push(wrapper)
    }
  }
  // A lista fica vermelha quando um item tem erro: vale o item, que diz qual é.
  const innermost = wrappers.filter(w => !wrappers.some(other => other !== w && w.contains(other)))
  return innermost
    .map(wrapper => ({
      label: labelOf(wrapper),
      tab: tabOf(wrapper, doc),
      item: itemOf(wrapper.querySelector('[name]')?.getAttribute('name')),
      message: errorTextOf(wrapper) || undefined,
    }))
    .filter(field => field.label)
}

/** Abas com erro (cabeçalho vermelho), para quando nenhum campo foi identificado. */
export const collectInvalidTabs = (doc: Document | undefined = currentDocument()): string[] =>
  doc
    ? Array.from(doc.querySelectorAll('.form-tab.error')).map(tab => cleanLabel(textOf(tab))).filter(Boolean)
    : []

/** "Longitude, item 2 (aba GPS)" */
export const describeField = (field: FieldOnScreen): string => {
  let text = field.label
  if (field.item) text = __t('%{campo}, item %{n}', { campo: text, n: field.item })
  if (field.tab) text = __t('%{campo} (aba %{aba})', { campo: text, aba: field.tab })
  return text
}

export const MAX_LINES = 8

/** Uma linha por campo, em lista; corta em MAX_LINES para o aviso caber na tela. */
export const composeFieldList = (lines: string[]): string | undefined => {
  if (!lines.length) return undefined
  const shown = lines.slice(0, MAX_LINES).map(line => `• ${line}`)
  if (lines.length > MAX_LINES) {
    shown.push(`• ${__t('e mais %{n} campo(s)', { n: lines.length - MAX_LINES })}`)
  }
  return [__t('Revise antes de salvar:'), ...shown].join('\n')
}

/** Texto do aviso quando a validação da própria tela barrou o salvar; undefined se não achou nada. */
export const explainInvalidForm = (doc: Document | undefined = currentDocument()): string | undefined => {
  const fields = collectInvalidFields(doc)
  if (fields.length) {
    return composeFieldList(fields.map(field => `${describeField(field)}: ${field.message || __t('Valor inválido')}`))
  }
  const tabs = collectInvalidTabs(doc)
  return tabs.length ? __t('Revise os campos em vermelho nas abas: %{abas}', { abas: tabs.join(', ') }) : undefined
}

export const INVALID_FORM_MESSAGE = 'ra.message.invalid_form'
/** Lista de campos leva mais que os 4 s padrão para ler. O MUI pausa o tempo com o mouse em cima. */
export const LIST_HIDE_DURATION = 12000

/**
 * Passa por aqui toda notificação antes de aparecer (FormErrorNotification):
 * - "formulário inválido" genérico do react-admin vira a lista dos campos em vermelho;
 * - mensagem em várias linhas (a lista, inclusive a do erro do servidor) ganha multiLine e mais tempo.
 * O resto passa igual.
 */
export const explainNotification = <N extends NotificationPayload | undefined>(
  notification: N,
  doc: Document | undefined = currentDocument(),
): N => {
  if (!notification || typeof notification.message !== 'string') return notification
  const message = notification.message === INVALID_FORM_MESSAGE
    ? explainInvalidForm(doc) ?? notification.message
    : notification.message
  if (!message.includes('\n')) return notification
  const options = notification.notificationOptions ?? {}
  return {
    ...notification,
    message,
    notificationOptions: {
      ...options,
      multiLine: true,
      autoHideDuration: options.autoHideDuration === null
        ? null
        : Math.max(options.autoHideDuration ?? 0, LIST_HIDE_DURATION),
      // O texto já vem traduzido: `_` faz o translate da Notification devolvê-lo como está.
      messageArgs: { _: message, ...options.messageArgs },
    },
  }
}
